import { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, Chip, Paper, Grid, Alert, CircularProgress, MenuItem, TextField,
  IconButton, Tooltip, Accordion, AccordionSummary, AccordionDetails, Divider, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import UndoIcon from "@mui/icons-material/Undo";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import { carePlanService, carePlanErrorMessage } from "../../services/carePlanService";
import CarePlanEditorDialog, { MEASUREMENTS, WEEKDAYS } from "./CarePlanEditorDialog";
import CarePlanTaskDialog from "./CarePlanTaskDialog";

const STATUS_COLOR = { ACTIVE: "success", DRAFT: "info", ARCHIVED: "default" };
const GOAL_COLOR = { OPEN: "default", MET: "success", DROPPED: "default" };
const EVENT_LABELS = {
  PLAN_CREATED: "Plan created",
  PLAN_UPDATED: "Plan updated",
  PLAN_ACTIVATED: "Plan activated",
  PLAN_ARCHIVED: "Plan archived",
  TASK_ADDED: "Task added",
  TASK_COMPLETED: "Task completed",
  TASK_REOPENED: "Task reopened",
  TASK_CANCELLED: "Task cancelled",
  TASK_NOTE_UPDATED: "Task note",
};

const measurementLabel = (type) => MEASUREMENTS.find((m) => m.type === type)?.label || type;
const weekdayLabel = (n) => WEEKDAYS.find((d) => d.value === n)?.label || "";

// Date-only values (YYYY-MM-DD) are built as local calendar dates so the
// browser's timezone can never shift them by a day.
const fmtDate = (s) => {
  if (!s) return "—";
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
};
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString() : "");

/**
 * Care plan tab on the patient profile (care team view). Create/edit a plan,
 * activate or archive it, manage tasks, and read the full history. The backend
 * enforces every rule (one active plan, archived plans are frozen, practice
 * scoping) — this screen only presents and drives them, and shows the server's
 * own message when something is refused.
 *
 * After every change it fires a "careplan:changed" window event, which the
 * adherence banner at the top of the profile listens for.
 */
export default function CarePlanTab({ patientId }) {
  const [plans, setPlans] = useState(null); // null = still loading
  const [selectedId, setSelectedId] = useState(null);
  const [staff, setStaff] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState({ open: false, plan: null });
  const [taskDialog, setTaskDialog] = useState({ open: false, mode: "add", task: null });
  const [confirm, setConfirm] = useState(null); // "activate" | "archive" | null
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);

  const selected = plans ? plans.find((p) => p.id === selectedId) || null : null;
  const archived = selected?.status === "ARCHIVED";
  const hasOtherActive = !!plans && plans.some((p) => p.status === "ACTIVE" && p.id !== selected?.id);
  const todayStr = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD, local

  const loadPlans = useCallback(
    async (preferId) => {
      try {
        const data = await carePlanService.list(patientId);
        setPlans(data);
        setSelectedId((prev) => {
          const wanted = preferId ?? prev;
          if (wanted && data.some((p) => p.id === wanted)) return wanted;
          const pick = data.find((p) => p.status === "ACTIVE") || data.find((p) => p.status === "DRAFT") || data[0];
          return pick ? pick.id : null;
        });
      } catch (err) {
        setError(carePlanErrorMessage(err, "Couldn't load care plans."));
        setPlans([]);
      }
    },
    [patientId]
  );

  useEffect(() => {
    setPlans(null);
    setSelectedId(null);
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    carePlanService
      .assignableStaff(patientId)
      .then(setStaff)
      .catch(() => setStaff([]));
  }, [patientId]);

  useEffect(() => {
    if (!historyOpen || !selectedId) return;
    let cancelled = false;
    carePlanService
      .history(patientId, selectedId)
      .then((d) => !cancelled && setHistory(d))
      .catch(() => !cancelled && setHistory([]));
    return () => {
      cancelled = true;
    };
  }, [patientId, selectedId, historyOpen, historyVersion]);

  // Runs one change, then refreshes everything that depends on it. `action` may
  // return a plan id to switch the view to (e.g. the plan just created).
  const run = async (action, successMessage) => {
    setBusy(true);
    setError(null);
    try {
      const preferId = await action();
      await loadPlans(preferId);
      setHistoryVersion((v) => v + 1);
      window.dispatchEvent(new Event("careplan:changed"));
      setNotice(successMessage);
      return true;
    } catch (err) {
      setError(carePlanErrorMessage(err, "Something went wrong. Nothing was changed."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const savePlan = async (body) => {
    const editing = editor.plan;
    const ok = await run(async () => {
      const result = editing
        ? await carePlanService.update(patientId, editing.id, body)
        : await carePlanService.create(patientId, body);
      return result.id;
    }, editing ? "Plan saved" : "Draft plan created");
    if (ok) setEditor({ open: false, plan: null });
  };

  const confirmAction = async () => {
    const type = confirm;
    await run(async () => {
      const result =
        type === "activate"
          ? await carePlanService.activate(patientId, selected.id)
          : await carePlanService.archive(patientId, selected.id);
      return result.id;
    }, type === "activate" ? "Plan activated" : "Plan archived");
    setConfirm(null); // on failure the error shows in the banner above
  };

  const setTaskStatus = (task, status, message) =>
    run(async () => {
      await carePlanService.updateTask(patientId, selected.id, task.id, { status });
    }, message);

  const saveTask = async (payload) => {
    const { mode, task } = taskDialog;
    const ok = await run(async () => {
      if (mode === "note") await carePlanService.updateTask(patientId, selected.id, task.id, { notes: payload.notes });
      else await carePlanService.addTask(patientId, selected.id, payload);
    }, mode === "note" ? "Note saved" : "Task added");
    if (ok) setTaskDialog({ open: false, mode: "add", task: null });
  };

  const openNewPlan = () => {
    setError(null);
    setEditor({ open: true, plan: null });
  };

  if (plans === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && !editor.open && !taskDialog.open && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
        {plans.length > 1 ? (
          <TextField select size="small" label="Plan" value={selectedId ?? ""} sx={{ minWidth: 320 }}
            onChange={(e) => setSelectedId(e.target.value)}>
            {plans.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.title} · {p.status.toLowerCase()}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Typography variant="h6" fontWeight={700}>Care plan</Typography>
        )}
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNewPlan}>New plan</Button>
      </Box>

      {!selected ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>No care plan yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Create a plan with goals, a monitoring schedule and tasks. Adherence is only tracked once you activate it.
          </Typography>
          <Button variant="contained" onClick={openNewPlan}>Create care plan</Button>
        </Paper>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography variant="h6" fontWeight={700}>{selected.title}</Typography>
                  <Chip size="small" color={STATUS_COLOR[selected.status]} label={selected.status.toLowerCase()}
                    sx={{ textTransform: "capitalize", fontWeight: 700 }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Start {fmtDate(selected.startDate)} · Next review {fmtDate(selected.nextReviewDate)} · Created by{" "}
                  {selected.createdByName || "—"}, {fmtDateTime(selected.createdAt)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
                {!archived && (
                  <Button size="small" startIcon={<EditIcon />} disabled={busy}
                    onClick={() => { setError(null); setEditor({ open: true, plan: selected }); }}>
                    Edit
                  </Button>
                )}
                {selected.status === "DRAFT" && (
                  <Button size="small" variant="contained" color="success" startIcon={<PlayArrowIcon />}
                    disabled={busy} onClick={() => setConfirm("activate")}>
                    Activate
                  </Button>
                )}
                {!archived && (
                  <Button size="small" color="inherit" startIcon={<ArchiveOutlinedIcon />} disabled={busy}
                    onClick={() => setConfirm("archive")}>
                    Archive
                  </Button>
                )}
              </Box>
            </Box>
            {selected.status === "DRAFT" && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This is a draft. It isn't the patient's active plan, and their adherence isn't measured against it, until you activate it.
              </Alert>
            )}
            {archived && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This plan is archived and can't be changed. Its tasks and history stay visible.
              </Alert>
            )}
            {selected.notes && (
              <Typography variant="body2" sx={{ mt: 2, whiteSpace: "pre-wrap" }}>{selected.notes}</Typography>
            )}
          </Paper>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Goals</Typography>
                {selected.goals.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No goals set.</Typography>
                ) : (
                  selected.goals.map((g) => (
                    <Box key={g.id} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.75 }}>
                      <Box>
                        <Typography variant="body2">{g.description}</Typography>
                        {g.targetDate && (
                          <Typography variant="caption" color="text.secondary">Target {fmtDate(g.targetDate)}</Typography>
                        )}
                      </Box>
                      <Chip size="small" variant={g.status === "OPEN" ? "outlined" : "filled"} color={GOAL_COLOR[g.status]}
                        label={g.status.toLowerCase()} sx={{ textTransform: "capitalize" }} />
                    </Box>
                  ))
                )}
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Monitoring schedule</Typography>
                {selected.monitoring.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No readings required.</Typography>
                ) : (
                  selected.monitoring.map((m) => (
                    <Box key={m.id} sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
                      <Typography variant="body2">{measurementLabel(m.measurementType)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {m.frequency === "WEEKLY" ? `Weekly · ${weekdayLabel(m.dayOfWeek)}` : "Daily"}
                      </Typography>
                    </Box>
                  ))
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Symptom and safety questions are always on the daily check-in.
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>Tasks</Typography>
              <Button size="small" startIcon={<AddIcon />} disabled={archived || busy}
                onClick={() => { setError(null); setTaskDialog({ open: true, mode: "add", task: null }); }}>
                Add task
              </Button>
            </Box>
            {selected.tasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No tasks yet.</Typography>
            ) : (
              selected.tasks.map((t, idx) => {
                const closed = t.status === "DONE" || t.status === "CANCELLED";
                const overdue = t.status === "OPEN" && t.dueDate && t.dueDate < todayStr;
                return (
                  <Box key={t.id}>
                    {idx > 0 && <Divider />}
                    <Box sx={{ display: "flex", gap: 1.5, py: 1.5, alignItems: "flex-start" }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ textDecoration: closed ? "line-through" : "none", color: closed ? "text.secondary" : "text.primary", fontWeight: 600 }}>
                          {t.title}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", my: 0.5 }}>
                          <Chip size="small" variant="outlined" color={t.assigneeType === "PATIENT" ? "primary" : "default"} label={t.assigneeName || "Unassigned"} />
                          {t.patientAdded && <Chip size="small" color="secondary" variant="outlined" label="Added by patient" />}
                          {t.dueDate && (
                            <Chip size="small" variant={overdue ? "filled" : "outlined"} color={overdue ? "error" : "default"}
                              label={`${overdue ? "Overdue · " : "Due "}${fmtDate(t.dueDate)}`} />
                          )}
                          {t.status !== "OPEN" && (
                            <Chip size="small" color={t.status === "DONE" ? "success" : "default"} label={t.status.toLowerCase()}
                              sx={{ textTransform: "capitalize" }} />
                          )}
                        </Box>
                        {t.description && <Typography variant="body2" color="text.secondary">{t.description}</Typography>}
                        {t.notes && <Typography variant="body2" sx={{ fontStyle: "italic", mt: 0.5 }}>Note: {t.notes}</Typography>}
                        {t.status === "DONE" && t.completedAt && (
                          <Typography variant="caption" color="text.secondary">
                            Completed {fmtDateTime(t.completedAt)}{t.completedByName ? ` by ${t.completedByName}` : ""}
                          </Typography>
                        )}
                      </Box>
                      {!archived && (
                        <Box sx={{ display: "flex", flexShrink: 0 }}>
                          {t.status === "OPEN" && (
                            <Tooltip title="Mark complete">
                              <span><IconButton size="small" color="success" disabled={busy}
                                onClick={() => setTaskStatus(t, "DONE", "Task completed")}><CheckCircleOutlineIcon /></IconButton></span>
                            </Tooltip>
                          )}
                          {t.status === "DONE" && (
                            <Tooltip title="Reopen">
                              <span><IconButton size="small" disabled={busy}
                                onClick={() => setTaskStatus(t, "OPEN", "Task reopened")}><UndoIcon /></IconButton></span>
                            </Tooltip>
                          )}
                          <Tooltip title={t.notes ? "Edit note" : "Add note"}>
                            <span><IconButton size="small" disabled={busy}
                              onClick={() => { setError(null); setTaskDialog({ open: true, mode: "note", task: t }); }}><NoteAddOutlinedIcon /></IconButton></span>
                          </Tooltip>
                          {t.status === "OPEN" && (
                            <Tooltip title="Cancel task">
                              <span><IconButton size="small" disabled={busy}
                                onClick={() => {
                                  if (window.confirm("Cancel this task? It stays in the plan history.")) {
                                    setTaskStatus(t, "CANCELLED", "Task cancelled");
                                  }
                                }}><CancelOutlinedIcon /></IconButton></span>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Paper>

          <Accordion variant="outlined" expanded={historyOpen} onChange={(e, expanded) => setHistoryOpen(expanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <HistoryIcon fontSize="small" />
                <Typography fontWeight={700}>History</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {history.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No history yet.</Typography>
              ) : (
                history.map((e, idx) => (
                  <Box key={e.id}>
                    {idx > 0 && <Divider sx={{ my: 1.25 }} />}
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                      <Typography variant="body2" fontWeight={700}>{EVENT_LABELS[e.eventType] || e.eventType}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {e.actorName || "Unknown"}{e.actorRole ? ` (${e.actorRole})` : ""} · {fmtDateTime(e.createdAt)}
                      </Typography>
                    </Box>
                    {e.detail && <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>{e.detail}</Typography>}
                  </Box>
                ))
              )}
            </AccordionDetails>
          </Accordion>
        </>
      )}

      <CarePlanEditorDialog open={editor.open} plan={editor.plan} saving={busy} error={error}
        onClose={() => setEditor({ open: false, plan: null })} onSave={savePlan} />
      <CarePlanTaskDialog open={taskDialog.open} mode={taskDialog.mode} task={taskDialog.task} staff={staff}
        saving={busy} error={error} onClose={() => setTaskDialog({ open: false, mode: "add", task: null })}
        onSave={saveTask} />

      <Dialog open={!!confirm} onClose={busy ? undefined : () => setConfirm(null)}>
        <DialogTitle>{confirm === "activate" ? "Activate this plan?" : "Archive this plan?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm === "activate"
              ? `It becomes the patient's active plan, and their adherence is measured against it.${
                  hasOtherActive ? " The plan that is currently active will be archived; it stays in the history." : ""
                }`
              : "Archived plans can't be edited or reactivated. The plan, its tasks and its history stay visible."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={confirmAction} disabled={busy}>
            {confirm === "activate" ? "Activate" : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!notice} autoHideDuration={3500} onClose={() => setNotice(null)}>
        <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>
      </Snackbar>
    </Box>
  );
}